import { useEffect, useState, useCallback } from 'react';
import { api, ApiError, type DriveItem } from '../lib/api';
import { iconFor } from '../lib/format';

interface TreeNodeProps {
  item: DriveItem;
  depth: number;
  selectedId: string | null;
  onSelect: (item: DriveItem) => void;
  filter: string;
}

function TreeNode({ item, depth, selectedId, onSelect, filter }: TreeNodeProps) {
  const [expanded, setExpanded] = useState(false);
  const [children, setChildren] = useState<DriveItem[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { items } = await api.folders(item.id);
      setChildren(items);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'failed');
    } finally {
      setLoading(false);
    }
  }, [item.id]);

  function toggle() {
    if (item.isFolder) {
      const next = !expanded;
      setExpanded(next);
      if (next && children === null) void load();
    } else {
      onSelect(item);
    }
  }

  const visibleChildren =
    children && filter
      ? children.filter((c) => c.name.toLowerCase().includes(filter.toLowerCase()))
      : children;

  const isSelected = selectedId === item.id;

  return (
    <div className="tree-node">
      <div
        className={`tree-row${isSelected ? ' selected' : ''}`}
        style={{ paddingLeft: 8 + depth * 14 }}
        onClick={toggle}
        role="treeitem"
        aria-expanded={item.isFolder ? expanded : undefined}
        aria-selected={isSelected}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggle();
          }
        }}
      >
        <span className="twisty">{item.isFolder ? (expanded ? '▾' : '▸') : ''}</span>
        <span className="tree-icon">{iconFor(item)}</span>
        <span className="tree-name" title={item.name}>
          {item.name}
        </span>
      </div>

      {item.isFolder && expanded && (
        <div role="group">
          {loading && <div className="tree-hint" style={{ paddingLeft: 8 + (depth + 1) * 14 }}>Loading…</div>}
          {error && <div className="tree-hint error" style={{ paddingLeft: 8 + (depth + 1) * 14 }}>{error}</div>}
          {visibleChildren?.map((c) => (
            <TreeNode
              key={c.id}
              item={c}
              depth={depth + 1}
              selectedId={selectedId}
              onSelect={onSelect}
              filter={filter}
            />
          ))}
          {visibleChildren && visibleChildren.length === 0 && !loading && (
            <div className="tree-hint" style={{ paddingLeft: 8 + (depth + 1) * 14 }}>
              {filter ? 'No matches' : 'Empty'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function FolderTree({
  selectedId,
  onSelect,
}: {
  selectedId: string | null;
  onSelect: (item: DriveItem) => void;
}) {
  const [roots, setRoots] = useState<DriveItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    void (async () => {
      try {
        const { items } = await api.folders();
        setRoots(items);
      } catch (err) {
        setError(err instanceof ApiError ? err.message : 'failed to load');
      }
    })();
  }, []);

  return (
    <div className="tree" role="tree" aria-label="Folders">
      <input
        className="tree-search"
        type="search"
        placeholder="Filter…"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        aria-label="Filter current folder"
      />
      {error && <div className="tree-hint error">{error}</div>}
      {roots === null && !error && <div className="tree-hint">Loading…</div>}
      {roots?.length === 0 && (
        <div className="tree-hint">
          No folders available. Ask an admin to share a folder with your account.
        </div>
      )}
      {roots?.map((r) => (
        <TreeNode
          key={r.id}
          item={r}
          depth={0}
          selectedId={selectedId}
          onSelect={onSelect}
          filter={filter}
        />
      ))}
    </div>
  );
}
